import { useState, useRef, Fragment, useEffect } from "react";
import Popup from "../../../hoc/Popup/Popup";

import "./EditMarkedRehabSlotsPopup.scss";
import { formatDateToSwedish } from "../../../shared/utility";
import greenCheck from "../../../assets/images/green-check.svg";
import { toast } from "react-toastify";
import {
  createRehabSlotComment,
  changeRehabSlotBookability,
} from "../../../api/rehab/rehab";
import { formatDate } from "../../../shared/dateUtils";

interface IEditMarkedRehabSlotsPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: () => void;
  selectedRehabSlots?: {
    date: Date;
    start_time: string;
    end_time: string;
  }[];
}

function EditMarkedRehabSlotsPopup(props: IEditMarkedRehabSlotsPopupProps) {
  const { selectedRehabSlots, showPopup, onClose, onConfirm } = props;
  const [actionsPerformed, setActionsPerformed] = useState<{
    madeBookable: boolean;
    madeUnbookable: boolean;
    commentAdded: boolean;
    commentText: string;
  }>({
    madeBookable: false,
    madeUnbookable: false,
    commentAdded: false,
    commentText: "",
  });
  const [comment, setComment] = useState("");
  const handleClose = () => {
    setActionsPerformed({
      madeBookable: false,
      madeUnbookable: false,
      commentAdded: false,
      commentText: "",
    });
    setComment("");
    onClose();
  };

  const handleCommentSlots = async () => {
    if (!selectedRehabSlots || selectedRehabSlots.length === 0) return;

    try {
      // Group slots by date to find all time intervals
      const allTimeSlots: { date: string; time: string }[] = [];

      // Group slots by date
      const slotsByDate = selectedRehabSlots.reduce((acc, slot) => {
        const dateStr = formatDate(slot.date, "yyyy-MM-dd");
        if (!acc[dateStr]) {
          acc[dateStr] = [];
        }
        acc[dateStr].push(slot.start_time);
        return acc;
      }, {} as Record<string, string[]>);

      // For each date, generate all 30-minute slots between earliest and latest
      Object.entries(slotsByDate).forEach(([date, times]) => {
        const sortedTimes = [...times].sort();
        const earliest = sortedTimes[0];
        const latest = sortedTimes[sortedTimes.length - 1];

        // Generate all 30-minute time slots between earliest and latest
        let currentTime = earliest;
        while (currentTime <= latest) {
          allTimeSlots.push({ date, time: currentTime });

          // Add 15 minutes
          const [hours, minutes] = currentTime.split(":").map(Number);
          const tempDate = new Date();
          tempDate.setHours(hours, minutes);
          tempDate.setMinutes(tempDate.getMinutes() + 15);
          currentTime = `${String(tempDate.getHours()).padStart(
            2,
            "0"
          )}:${String(tempDate.getMinutes()).padStart(2, "0")}:00`;
        }
      });

      const commentPromises = allTimeSlots.map((slot) => {
        const datetime = slot.date + "T" + slot.time;
        return createRehabSlotComment(datetime, comment);
      });

      const responses = await Promise.all(commentPromises);

      // Check if all responses are valid
      const allSuccessful = responses.every(
        (response) => response && !("message" in response)
      );

      if (allSuccessful) {
        toast.success("Kommentar sparad för alla intervall");
        setActionsPerformed((prev) => ({
          ...prev,
          commentAdded: true,
          commentText: comment,
        }));
        onConfirm();
        setComment("");
      } else {
        toast.warning("Vissa intervall kunde inte uppdateras");
      }
    } catch (error: any) {
      toast.error(error?.message || "Kunde inte spara kommentar");
    }
  };

  const handleChangeBookability = async (makeBookable: boolean) => {
    if (!selectedRehabSlots || selectedRehabSlots.length === 0) return;

    try {
      // Group slots by date to find all time intervals
      const allTimeSlots: { date: string; time: string }[] = [];

      // Group slots by date
      const slotsByDate = selectedRehabSlots.reduce((acc, slot) => {
        const dateStr = formatDate(slot.date, "yyyy-MM-dd");
        if (!acc[dateStr]) {
          acc[dateStr] = [];
        }
        acc[dateStr].push(slot.start_time);
        return acc;
      }, {} as Record<string, string[]>);

      // For each date, generate all 30-minute slots between earliest and latest
      Object.entries(slotsByDate).forEach(([date, times]) => {
        const sortedTimes = [...times].sort();
        const earliest = sortedTimes[0];
        const latest = sortedTimes[sortedTimes.length - 1];

        // Generate all 30-minute time slots between earliest and latest
        let currentTime = earliest;
        while (currentTime <= latest) {
          allTimeSlots.push({ date, time: currentTime });

          // Add 15 minutes
          const [hours, minutes] = currentTime.split(":").map(Number);
          const tempDate = new Date();
          tempDate.setHours(hours, minutes);
          tempDate.setMinutes(tempDate.getMinutes() + 15);
          currentTime = `${String(tempDate.getHours()).padStart(
            2,
            "0"
          )}:${String(tempDate.getMinutes()).padStart(2, "0")}:00`;
        }
      });

      const bookabilityPromises = allTimeSlots.map((slot) => {
        const datetime = slot.date + "T" + slot.time;
        return changeRehabSlotBookability(datetime, makeBookable);
      });

      await Promise.all(bookabilityPromises);
      toast.success(
        `Alla intervall har gjorts ${
          makeBookable ? "tillgängliga" : "otillgängliga"
        }`
      );
      onConfirm();
      setActionsPerformed((prev) => ({
        ...prev,
        madeBookable: makeBookable,
        madeUnbookable: !makeBookable,
      }));
    } catch (error: any) {
      toast.error(error?.message || "Kunde inte uppdatera tillgänglighet");
    }
  };

  return (
    <Popup
      showPopup={showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Redigera markerade slots med kommentar och tillgänglighet
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-top"></div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Påvärkade tider</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {selectedRehabSlots
                      ? (() => {
                          // Count all 30-minute slots that will be affected
                          const allTimeSlots: { date: string; time: string }[] =
                            [];

                          // Group slots by date
                          const slotsByDate = selectedRehabSlots.reduce(
                            (acc, slot) => {
                              const dateStr = formatDate(
                                slot.date,
                                "yyyy-MM-dd"
                              );
                              if (!acc[dateStr]) {
                                acc[dateStr] = [];
                              }
                              acc[dateStr].push(slot.start_time);
                              return acc;
                            },
                            {} as Record<string, string[]>
                          );

                          // Count all 30-minute slots between earliest and latest
                          Object.entries(slotsByDate).forEach(
                            ([date, times]) => {
                              const sortedTimes = [...times].sort();
                              const earliest = sortedTimes[0];
                              const latest =
                                sortedTimes[sortedTimes.length - 1];

                              let currentTime = earliest;
                              while (currentTime <= latest) {
                                allTimeSlots.push({ date, time: currentTime });

                                // Add 15 minutes
                                const [hours, minutes] = currentTime
                                  .split(":")
                                  .map(Number);
                                const tempDate = new Date();
                                tempDate.setHours(hours, minutes);
                                tempDate.setMinutes(tempDate.getMinutes() + 15);
                                currentTime = `${String(
                                  tempDate.getHours()
                                ).padStart(2, "0")}:${String(
                                  tempDate.getMinutes()
                                ).padStart(2, "0")}:00`;
                              }
                            }
                          );

                          return `${allTimeSlots.length} tider`;
                        })()
                      : ""}
                  </span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-form-slots">
                <span className="text-m primary-text">Valda tidsintervall</span>
                {selectedRehabSlots
                  ? (() => {
                      // Group slots by date
                      const slotsByDate = selectedRehabSlots.reduce(
                        (acc, slot) => {
                          const dateStr = formatDate(slot.date, "yyyy-MM-dd");
                          if (!acc[dateStr]) {
                            acc[dateStr] = [];
                          }
                          acc[dateStr].push(slot.start_time);
                          return acc;
                        },
                        {} as Record<string, string[]>
                      );

                      // For each date, show earliest and latest time
                      return Object.entries(slotsByDate).map(
                        ([date, times]) => {
                          const sortedTimes = [...times].sort();
                          const earliest = sortedTimes[0].slice(0, 5);
                          const latest = sortedTimes[
                            sortedTimes.length - 1
                          ].slice(0, 5);
                          const { weekday, dateAndMonth } = formatDateToSwedish(
                            new Date(date)
                          );

                          return (
                            <div
                              key={date}
                              className="general-popup-flex-between"
                            >
                              <span className="text-m">{dateAndMonth}</span>
                              <div className="text-m primary-text text-svg-wrapper">
                                <span>
                                  {earliest} - {latest}
                                </span>
                                <img src={greenCheck} alt="green check" />
                              </div>
                            </div>
                          );
                        }
                      );
                    })()
                  : "Inga intervall valda"}
              </div>
              {actionsPerformed.commentAdded && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Kommentar:</span>
                  <div className="text-m primary-text text-svg-wrapper">
                    <span> {actionsPerformed.commentText}</span>
                    <img src={greenCheck} alt="green check" />
                  </div>
                </div>
              )}
              {actionsPerformed.madeBookable && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Bokningsbar:</span>
                  <div className="text-m primary-text text-svg-wrapper">
                    <span>Gjord tillgänglig</span>
                    <img src={greenCheck} alt="green check" />
                  </div>
                </div>
              )}
              {actionsPerformed.madeUnbookable && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Bokningsbar:</span>
                  <div className="text-m primary-text text-svg-wrapper">
                    <span>Gjord otillgänglig</span>
                    <img src={greenCheck} alt="green check" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <span className="text-m">Kommentar</span>
              <input
                className="text-m"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              ></input>
              <div className="general-popup-flex-right text-m">
                <button type="button" onClick={() => handleCommentSlots()}>
                  Spara kommentar
                </button>
              </div>
              <div className="general-popup-flex-right text-m">
                <button
                  type="button"
                  onClick={() => handleChangeBookability(false)}
                  className="text-m"
                >
                  Gör otillgänglig
                </button>
              </div>
              <div className="general-popup-flex-right text-m">
                <button
                  type="button"
                  onClick={() => handleChangeBookability(true)}
                  className="text-m"
                >
                  Gör tillgänglig
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Popup>
  );
}

export default EditMarkedRehabSlotsPopup;
